import type { UseFormReturn } from 'react-hook-form';
import { Mail } from 'lucide-react';
import { FieldError, authFieldWrapClass } from '@/features/auth/components/FieldError';
import {
  LOGIN_EMAIL_MAX_LENGTH,
  getDigits,
  isEmailLike,
  isPhoneLikeIdentifier,
  validateLoginIdentifier,
  type LoginFormValues,
  type LoginMethod,
} from '@/features/auth/utils/loginIdentifier';
import {
  formatMobileDisplay,
  parseMobileInput,
  type MobileConfig,
} from '@/features/auth/utils/mobileNumber';

type LoginIdentifierFieldProps = {
  form: UseFormReturn<LoginFormValues>;
  loginMethod: LoginMethod;
  otpSent: boolean;
  phoneConfig: MobileConfig;
  isPhoneInput: boolean;
};

export function LoginIdentifierField({
  form,
  loginMethod,
  otpSent,
  phoneConfig,
  isPhoneInput,
}: LoginIdentifierFieldProps) {
  const {
    register,
    setValue,
    setError,
    clearErrors,
    watch,
    formState: { errors },
  } = form;

  const identifierValue = watch('identifier') || '';

  return (
    <div className="w-full min-w-0">
      <label className="mb-2 block text-[13px] font-bold leading-none text-[#2E2A54]">
        Email or phone
      </label>
      <div
        className={`${authFieldWrapClass(Boolean(errors.identifier))} ${
          loginMethod === 'otp' && otpSent ? 'opacity-70' : ''
        }`}
      >
        {isPhoneInput ? (
          <div
            className="flex h-full shrink-0 select-none items-center gap-1.5 border-r border-[#EAE8F5] bg-[#F3F2F9] px-2.5 text-[12px] font-semibold text-[#2E2A54]"
            style={{ fontFamily: "'JetBrains Mono', monospace" }}
          >
            <span>{phoneConfig.flag}</span>
            <span>{phoneConfig.code}</span>
          </div>
        ) : (
          <div className="pointer-events-none shrink-0 pl-3.5 text-[#8156B4]">
            <Mail size={18} strokeWidth={1.8} />
          </div>
        )}
        <input
          type="text"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="none"
          spellCheck={false}
          placeholder={
            isPhoneInput ? phoneConfig.placeholder : 'Mobile number or Email'
          }
          disabled={loginMethod === 'otp' && otpSent}
          inputMode={isPhoneInput ? 'numeric' : 'email'}
          maxLength={
            isPhoneInput ? phoneConfig.placeholder.length : LOGIN_EMAIL_MAX_LENGTH
          }
          className="h-full min-w-0 flex-1 border-none bg-transparent px-3 text-sm text-[#2E2A54] outline-none placeholder:text-[#A9A6BE] disabled:cursor-not-allowed"
          {...register('identifier', {
            required: 'Email or phone number is required',
            validate: (value) => validateLoginIdentifier(value),
          })}
          value={
            isPhoneInput
              ? formatMobileDisplay(identifierValue, phoneConfig)
              : identifierValue
          }
          onChange={(e) => {
            const raw = e.currentTarget.value;

            // Empty or email-like (has @ / letters) → keep as free text.
            if (!raw || isEmailLike(raw)) {
              setValue('identifier', raw, { shouldValidate: true });
              return;
            }

            // Digits / phone formatting chars only.
            if (!/^[\d\s+\-()]*$/.test(raw)) {
              setValue('identifier', raw, { shouldValidate: true });
              return;
            }

            const digits = getDigits(raw);

            // Ambiguous numeric start that is NOT a valid mobile first digit
            // (e.g. "111") — keep digits so the user can continue to an email
            // like 111@mailinator.com. Do not apply mobile parsing/rejection.
            if (!isPhoneLikeIdentifier(digits, phoneConfig)) {
              setValue('identifier', digits.slice(0, LOGIN_EMAIL_MAX_LENGTH), {
                shouldValidate: false,
              });
              clearErrors('identifier');
              return;
            }

            const { value, rejected } = parseMobileInput(raw, phoneConfig);
            setValue('identifier', value, {
              shouldValidate: rejected || value.length === phoneConfig.length,
            });
            if (rejected) {
              setError('identifier', {
                type: 'validate',
                message: phoneConfig.message,
              });
            } else if (value.length < phoneConfig.length) {
              clearErrors('identifier');
            }
          }}
        />
      </div>
      <FieldError message={errors.identifier?.message} />
    </div>
  );
}
